import * as React from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

const Pagination = ({ className, ...props }) => (
	<nav role="navigation" aria-label="pagination" className={cn('mx-auto flex w-full justify-center', className)} {...props} />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef(({ className, ...props }, ref) => (
	<ul ref={ref} className={cn('flex flex-row items-center gap-1', className)} {...props} />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef(({ className, ...props }, ref) => <li ref={ref} className={cn('', className)} {...props} />);
PaginationItem.displayName = 'PaginationItem';

const PaginationLink = ({ className, isActive, size = 'icon', ...props }) => (
	<a
		aria-current={isActive ? 'page' : undefined}
		className={cn(buttonVariants({ variant: isActive ? 'outline' : 'ghost', size }), className)}
		{...props}
	/>
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({ className, ...props }) => (
	<PaginationLink aria-label="Go to previous page" size="default" className={cn('gap-1 pl-2.5', className)} {...props}>
		<ChevronLeft className="h-4 w-4" />
		<span>Previous</span>
	</PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({ className, ...props }) => (
	<PaginationLink aria-label="Go to next page" size="default" className={cn('gap-1 pr-2.5', className)} {...props}>
		<span>Next</span>
		<ChevronRight className="h-4 w-4" />
	</PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }) => (
	<span aria-hidden className={cn('flex h-9 w-9 items-center justify-center', className)} {...props}>
		<MoreHorizontal className="h-4 w-4" />
		<span className="sr-only">More pages</span>
	</span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

// The visible page list for a numbered pager: ≤7 pages → all of them;
// otherwise a left-edge / right-edge / centred window with 'ellipsis' markers.
function buildPageNumbers(currentPage, totalPages) {
	if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
	const pages = [];
	const showLeftEdge = currentPage <= 4;
	const showRightEdge = currentPage >= totalPages - 3;
	if (showLeftEdge) {
		pages.push(1, 2, 3, 4, 5, 'ellipsis', totalPages);
	} else if (showRightEdge) {
		pages.push(1, 'ellipsis', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
	} else {
		pages.push(1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', totalPages);
	}
	return pages;
}

// The assembled numbered pager (Prev · 1 2 3 … N · Next) shared by the Orders
// page and the Manage Products footer. `compact` is the tight table-footer
// variant: h-8 controls and the short "Prev" label.
const NumberedPager = ({ page, totalPages, onPageChange, className, compact = false }) => (
	<Pagination className={className}>
		<PaginationContent>
			<PaginationItem>
				<PaginationLink
					href="#"
					size="default"
					aria-label="Go to previous page"
					aria-disabled={page === 1}
					onClick={(e) => {
						e.preventDefault();
						onPageChange(Math.max(1, page - 1));
					}}
					className={cn('gap-1 pl-2.5', compact && 'h-8', page === 1 && 'pointer-events-none opacity-50')}
				>
					<ChevronLeft className="h-4 w-4" />
					<span>{compact ? 'Prev' : 'Previous'}</span>
				</PaginationLink>
			</PaginationItem>
			{buildPageNumbers(page, totalPages).map((p, i) =>
				p === 'ellipsis' ? (
					<PaginationItem key={`ellipsis-${i}`}>
						<PaginationEllipsis className={cn(compact && 'h-8 w-8')} />
					</PaginationItem>
				) : (
					<PaginationItem key={p}>
						<PaginationLink
							href="#"
							isActive={p === page}
							onClick={(e) => {
								e.preventDefault();
								onPageChange(p);
							}}
							className={cn('tabular-nums', compact && 'h-8 w-8')}
						>
							{p}
						</PaginationLink>
					</PaginationItem>
				)
			)}
			<PaginationItem>
				<PaginationLink
					href="#"
					size="default"
					aria-label="Go to next page"
					aria-disabled={page === totalPages}
					onClick={(e) => {
						e.preventDefault();
						onPageChange(Math.min(totalPages, page + 1));
					}}
					className={cn('gap-1 pr-2.5', compact && 'h-8', page === totalPages && 'pointer-events-none opacity-50')}
				>
					<span>Next</span>
					<ChevronRight className="h-4 w-4" />
				</PaginationLink>
			</PaginationItem>
		</PaginationContent>
	</Pagination>
);
NumberedPager.displayName = 'NumberedPager';

export {
	NumberedPager,
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	buildPageNumbers
};
